// EditableField Component - Inline editable text field with click-to-edit behavior

import React, { useState, useRef, useEffect } from 'react';
import { TextField, useTheme } from '@fluentui/react';
import type { ITextFieldProps } from '@fluentui/react';

interface EditableFieldProps {
  value: string;
  onChange: (newValue: string) => void;
  placeholder?: string;
  multiline?: boolean;
  rows?: number;
  disabled?: boolean;
  textFieldProps?: Partial<ITextFieldProps>;
}

const EditableField: React.FC<EditableFieldProps> = ({
  value,
  onChange,
  placeholder,
  multiline = false,
  rows,
  disabled = false,
  textFieldProps,
}) => {
  const theme = useTheme();
  const [isEditing, setIsEditing] = useState(false);
  const [currentValue, setCurrentValue] = useState(value);
  const textFieldRef = useRef<any>(null);

  useEffect(() => {
    setCurrentValue(value);
  }, [value]);

  useEffect(() => {
    if (isEditing && textFieldRef.current) {
      textFieldRef.current.focus();
    }
  }, [isEditing]);

  const handleTextClick = () => {
    if (isEditing || disabled) return;
    setIsEditing(true);
  };

  const handleBlur = () => {
    setIsEditing(false);
    if (onChange && currentValue !== value) {
      onChange(currentValue);
    }
  };

  const handleChange = (_e: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => {
    setCurrentValue(newValue || '');
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!multiline && e.key === 'Enter') {
      e.preventDefault();
      handleBlur();
    } else if (e.key === 'Escape') {
      setCurrentValue(value);
      setIsEditing(false);
    }
  };

  return (
    <div
      onClick={handleTextClick}
      style={{
        minHeight: '40px',
        minWidth: '100px',
        border: `1px solid ${theme.palette.neutralLight}`,
        padding: '8px',
        borderRadius: '2px',
        cursor: disabled ? 'default' : 'pointer',
        backgroundColor: disabled ? theme.palette.neutralLighter : 'transparent',
      }}
    >
      {isEditing ? (
        <TextField
          componentRef={textFieldRef}
          value={currentValue}
          onChange={handleChange}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          multiline={multiline}
          rows={rows}
          borderless
          styles={{
            root: { margin: '-8px' },
            field: { padding: '8px' },
          }}
          {...textFieldProps}
        />
      ) : (
        <span
          style={{
            color: theme.palette.neutralPrimary,
            whiteSpace: multiline ? 'pre-wrap' : 'nowrap',
          }}
        >
          {currentValue || placeholder}
        </span>
      )}
    </div>
  );
};

export default EditableField;
